// External
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@toddle-edu/ds-web";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { CaretDownFilled, StarsTwoFilled } from "@toddle-edu/ds-icons";
import { graphql, compose } from "lib/apolloClient";

// Internal
import AILanguageSelection from "AppComponents/ConfigureAIVoice/components/AILanguageSelection";
import { useWorksheetAiConstantPrompts } from "Worksheet/hooks/useWorksheetAiConstantPrompts";
import { WORKSHEET } from "Constants/stringConstants";
import I18nHOC from "UIComponents/I18nHOC";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { useDebouncedCallback } from "Utils/CustomHooks";
import { getIsFromClassroom } from "UnitPlans/modules/Utils";
import { getAssessmentQuery } from "Assessments/modules/AssessmentQuery";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { updateGeneratingAIQuestions } from "Worksheet/modules/WorksheetModules";
import {
  getAssessmentQuestionsEnhancer,
  getAssessmentQuestionsEnhancerV2,
} from "Worksheet/modules/WorksheetEnhancers";
import { getAddLearningGoalsDropdownOptions } from "UnitPlans/components/LibraryCommonComponents/ResourceEdit/modules/Utils";
import { FIELD_UID_ADD_LEARNING_GOALS } from "Worksheet/modules/WorksheetConstants";
import {
  getUserType,
  getSelectedCurriculumId,
  getSelectedAcademicYearId,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";
import {
  getAiConversationId,
  getOptionsFromField,
  getAssessmentQuestionsQueryVariables,
} from "Worksheet/modules/WorksheetUtils";
import {
  getFieldValue,
  getDynamicTemplate,
  updateAssessmentTemplate,
  getUnitPlanFromAssessment,
  getUnitDetailsFromUnitPlanField,
  changeDisciplinaryContentToMultiField,
} from "Assessments/modules/Utils";
import { getPlannerTemplateFromCache } from "modules/CommonGraphqlHelpers";
import {
  updateAIConversation,
  getVariablesForLocalisedPlannerTemplates,
} from "modules/Services";
import {
  onSendQuery,
  cancelRequest,
} from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/AiQuestionAssistantModule";
import { getTemplateArray } from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/Utils";
import { getAIConversationsFromCache } from "Worksheet/modules/WorksheetGraphqlHelpers";
import { setToastMsg } from "Login/modules/LoginModule";

// Relative
import classes from "./AiQuestionGeneratorV2.scss";
import QuestionLoader from "./QuestionLoader";
import AiQuestionLoader from "./AiQuestionLoader";
import AddMediaReference from "./AddMediaReference";
import LearningManager from "../ExtractFromDoc/LearningManager";
import AiQuestionGeneratorQuestionDetails from "./AiQuestionGeneratorQuestionDetails";
import AiQuestionGeneratorCustomInstruction from "./AiQuestionGeneratorCustomInstruction";
import { CONTEXT_FIELDS } from "./AiQuestionGeneratorConstants";
import {
  mergeFields,
  getInitialContextData,
  getOnSendQueryPayload,
  getTargetedContentField,
  getCustomInstructionData,
  resetMessagesInAIConversations,
  getSanitizedVisualizationInput,
  getIsNewWorksheetAIStreamingEnabled,
} from "./AiQuestionGeneratorUtils";

const {
  CF_TYPE_OF_QUESTIONS,
  CF_NUMBER_OF_QUESTIONS,
  CF_AI_INSTRUCTIONS,
} = CONTEXT_FIELDS;

/**
 *
 * @returns AiQuestionGeneratorV2 Component
 */
const AiQuestionGeneratorV2 = props => {
  const {
    data: sections,
    aiConversationId,
    gradeId,
    curriculumType,
    conversationMetadata,
    conversationAttachments,
    updateAIConversation,
    updateGeneratingAIQuestions,
    onSendQuery,
    learningGoalsFields,
    isLoading,
    worksheetMutationData,
    toggleAiQuestionGenerator,
    getAssessmentQuestions,
    addSection,
    sourceFrom,
    classSubjects,
    mode,
    onSendQueryProps,
    currentDebugInfoMetaData,
    preStepCurrentDebugInfoMetaData,
    descriptionField,
    setToastMsg,
    unitPlanDetails,
    assessmentTemplate,
    aiPromptFromMetaData,
    assessmentId,
    assessmentDetails,
  } = props;

  const { t } = useTranslation();

  const {
    data: { actionPrompts, presetPrompts },
  } = useWorksheetAiConstantPrompts({
    assessmentDetails,
    entityType: WORKSHEET,
    entityId: assessmentId,
  });

  const [currentAIState, setCurrentAIState] = useState("");
  const [showQuestionLoader, setShowQuestionLoader] = useState(false);
  const [aiVoiceModalLanguage, setAiVoiceModalLanguage] = useState("en");
  const [learningGoalsFieldsData, setLearningGoalsFieldsData] = useState([]);
  const [inProgressAttachments, setInProgressAttachments] = useState([]);
  const [step, setStep] = useState(1);

  const [contextData, setContextData] = useState(() =>
    getInitialContextData({ conversationMetadata, curriculumType })
  );

  // * Details are set from Learning Manager Component as callback to prevent redundancy
  const [
    updatedAssessmentAndUnitPlanDetails,
    setUpdatedAssessmentAndUnitPlanDetails,
  ] = useState({});

  // * Saving metadata in AI conversation
  const [updateMetaDataInAiConversation] = useDebouncedCallback(
    ({ key, identifier, newFields }) => {
      const aiConversation = getAIConversationsFromCache({
        id: aiConversationId,
      });
      const _conversationMetadata = _.get(aiConversation, "metadata", {});

      const updatedConversationMetadata = {
        ...(_conversationMetadata || {}),
        [key]: mergeFields({
          identifier,
          newFields,
          existingFields: [],
        }),
      };
      const debugInfo = _.get(currentDebugInfoMetaData, aiConversationId, null);
      const preStepDebugInfo = _.get(
        preStepCurrentDebugInfoMetaData,
        aiConversationId,
        null
      );
      if (debugInfo) {
        // * if latest debug data available in redux, then updating it in metadata
        updatedConversationMetadata.debugInfo = debugInfo;
        if (preStepDebugInfo) {
          updatedConversationMetadata.preStepDebugInfo = preStepDebugInfo;
        }
      }
      updateAIConversation({
        input: {
          id: aiConversationId,
          metadata: updatedConversationMetadata,
        },
      });
    },
    300,
    [
      aiConversationId,
      updateAIConversation,
      conversationMetadata,
      currentDebugInfoMetaData,
      preStepCurrentDebugInfoMetaData,
    ]
  );

  // * Updating local context data
  const onUpdateLocalData = (key, value) => {
    const updatedContextData = { ...contextData, [key]: value };
    setContextData(updatedContextData);

    // * Updating local fields
    const localFields = _.map(updatedContextData, (value, field) => ({
      field,
      value,
    }));

    // * Saving local fields in AI conversation
    updateMetaDataInAiConversation({
      key: "localFields",
      identifier: "field",
      newFields: localFields,
    });
  };

  // * Saving Learning goals fields in AI conversation
  const onUpdateLearningGoals = ({ newLearningGoalsFieldData }) => {
    setLearningGoalsFieldsData(newLearningGoalsFieldData);
    updateMetaDataInAiConversation({
      key: "allFields",
      identifier: "uid",
      newFields: newLearningGoalsFieldData,
    });
  };

  // * Changes the language in the AI voice modal
  const setConfigureAIVoiceRef = useCallback(node => {
    if (!node) return;
    const { preferredLanguageOptions, preferredVoiceObj } = node || {};
    const prefLang = _.get(preferredVoiceObj, "language", "en");
    const languageKey = _.find(preferredLanguageOptions, { key: prefLang });
    if (languageKey?.lng) setAiVoiceModalLanguage(languageKey?.lng);
  }, []);

  // * Checking is all required data is available to generate questions
  const isGenerateEnabled = useMemo(() => {
    const instruction = contextData[CF_AI_INSTRUCTIONS];
    const hasInstructions =
      !_.isEmpty(_.get(instruction, "data")) ||
      !_.isEmpty(_.get(instruction, "selectedPrompt"));
    const hasAttachments = !_.isEmpty(conversationAttachments);
    const anyInProgress = !_.isEmpty(inProgressAttachments);
    const hasLearningGoal = !_.isEmpty(learningGoalsFieldsData);

    if (anyInProgress) return false;
    return hasInstructions || hasAttachments || hasLearningGoal;
  }, [
    contextData,
    conversationAttachments,
    inProgressAttachments,
    learningGoalsFieldsData,
  ]);

  // * Targeted content based on curriculum type
  const TARGETED_CONTENT = useMemo(() => {
    return getTargetedContentField(curriculumType);
  }, [curriculumType]);

  // * Generate questions with Toddle AI
  const onGenerateQuestions = async (
    currentStep,
    worksheetMarkdown,
    visualizationInput = null
  ) => {
    updateGeneratingAIQuestions(true);

    // * Adding section if not exists
    const lastSection = _.last(sections);
    if (!lastSection && !worksheetMarkdown) {
      const { sectionId } = await addSection();
      if (!sectionId) {
        updateGeneratingAIQuestions(true);
        setShowQuestionLoader(false);
        return;
      }
    }

    // * Mapping context data as per payload requirements
    const context = {
      [CF_AI_INSTRUCTIONS]: getCustomInstructionData(contextData),
    };

    const typesOfQuestions = _.join(
      _.without(contextData[CF_TYPE_OF_QUESTIONS], "AUTO"),
      ", "
    );
    if (!_.isEmpty(typesOfQuestions)) {
      context[CF_TYPE_OF_QUESTIONS] = typesOfQuestions;
    }

    const numberOfQuestions = contextData[CF_NUMBER_OF_QUESTIONS];
    if (numberOfQuestions > 0) {
      context[CF_NUMBER_OF_QUESTIONS] = _.toString(numberOfQuestions || 0);
    }

    if (!_.isEmpty(contextData[TARGETED_CONTENT])) {
      context[TARGETED_CONTENT] = contextData[TARGETED_CONTENT];
    }

    if (worksheetMarkdown) {
      context["worksheetMarkdown"] = worksheetMarkdown;
    }
    if (visualizationInput) {
      context["visualizationInput"] = getSanitizedVisualizationInput(
        visualizationInput
      );
    }

    setShowQuestionLoader(true);

    try {
      // * API Call for aiResponse
      await onSendQuery({
        t,
        setCurrentAIState,
        context,
        selectedAction:
          currentStep === 1 && !worksheetMarkdown
            ? _.first(actionPrompts)
            : _.last(actionPrompts),
        learningGoalsFields,
        conversationAttachments,
        language: aiVoiceModalLanguage,

        ...onSendQueryProps,
        ...getOnSendQueryPayload({
          learningGoalsFieldsData,
          updatedAssessmentAndUnitPlanDetails,
        }),
        shouldNotProcessAttachments: currentStep === 2,
      });
    } catch (error) {
      setShowQuestionLoader(false);
    }
  };

  // * On completing first prompt response, call second prompt to generate questions
  const handleGoToSecondStep = (
    worksheetMarkdown,
    visualizationInput = null
  ) => {
    setStep(2);
    onGenerateQuestions(2, worksheetMarkdown, visualizationInput);
  };

  // * Resetting AI history on re-mounting
  const resetAIHistory = (error = false) => {
    if (error) {
      setToastMsg("toastMsgs:question_generation_failed");
    }
    resetMessagesInAIConversations({ aiConversationId });
    setCurrentAIState("");
    setShowQuestionLoader(false);
  };

  // * Resetting AI history and cancelling if any on-going request on mounting
  useEffect(() => {
    resetAIHistory();
    return () => {
      cancelRequest?.();
    };
  }, []);

  // * Updating AI instructions when aiPromptFromMetaData is present in assessment details metaData
  useEffect(() => {
    if (!_.isEmpty(aiPromptFromMetaData)) {
      onUpdateLocalData(CF_AI_INSTRUCTIONS, {
        selectedPrompt: null,
        data: aiPromptFromMetaData,
      });
    }
  }, [aiPromptFromMetaData]);

  const QuestionStreamingComponent = getIsNewWorksheetAIStreamingEnabled()
    ? AiQuestionLoader
    : QuestionLoader;

  return (
    <div className={classes.container}>
      {isLoading ? <FullScreenLoader /> : null}

      <div className={classes.contentContainer}>
        {showQuestionLoader && (
          <QuestionStreamingComponent
            t={t}
            step={step}
            gradeId={gradeId}
            sections={sections}
            currentAIState={currentAIState}
            streamingState={currentAIState}
            resetAIHistory={resetAIHistory}
            descriptionField={descriptionField}
            aiConversationId={aiConversationId}
            setCurrentAIState={setCurrentAIState}
            setStreamingState={setCurrentAIState}
            goToSecondStep={handleGoToSecondStep}
            worksheetMutationData={worksheetMutationData}
            getAssessmentQuestions={getAssessmentQuestions}
            toggleAiQuestionGenerator={toggleAiQuestionGenerator}
            onGenerateComplete={({ closeAiPanel = false } = {}) => {
              setStep(1);
              updateGeneratingAIQuestions(false);
              resetAIHistory();
              if (closeAiPanel) {
                toggleAiQuestionGenerator();
              }
            }}
          />
        )}

        {!showQuestionLoader && (
          <>
            <AiQuestionGeneratorCustomInstruction
              t={t}
              onChange={onUpdateLocalData}
              presetPrompts={presetPrompts}
              instruction={contextData[CF_AI_INSTRUCTIONS]}
              aiConversationId={aiConversationId}
            />

            <AddMediaReference
              aiConversationId={aiConversationId}
              attachments={conversationAttachments}
              inProgressAttachments={inProgressAttachments}
              setInProgressAttachments={setInProgressAttachments}
            />

            <AiQuestionGeneratorQuestionDetails
              t={t}
              questionTypes={contextData[CF_TYPE_OF_QUESTIONS]}
              questionCount={contextData[CF_NUMBER_OF_QUESTIONS]}
              onChange={onUpdateLocalData}
            />

            <LearningManager
              assessmentId={assessmentId}
              toggleAiQuestionGenerator={toggleAiQuestionGenerator}
              sourceFrom={sourceFrom}
              classSubjects={classSubjects}
              mode={mode}
              context={contextData}
              setContext={contextData}
              onChangeLearningGoals={onUpdateLearningGoals}
              conversationMetadata={conversationMetadata}
              onChangeTargetedContent={onUpdateLocalData}
              setUpdatedAssessmentAndUnitPlanDetails={
                setUpdatedAssessmentAndUnitPlanDetails
              }
              unitPlanDetails={unitPlanDetails}
              assessmentTemplate={assessmentTemplate}
              isLoading={isLoading}
            />

            <div className={classes.footer}>
              <AILanguageSelection
                mode="edit"
                gradeId={gradeId}
                buttonSize="medium"
                entityType="ASSESSMENT"
                conversationId={aiConversationId}
                customRef={setConfigureAIVoiceRef}
                conversationMetadata={conversationMetadata}
                buttonProps={{
                  rightIcon: <CaretDownFilled />,
                  className: classes.languageSelectionButton,
                }}
              />
              <Button
                dsVersion="2.0"
                onClick={() => onGenerateQuestions(1)}
                variant={"progressive"}
                type={isGenerateEnabled ? "fill" : "outlined"}
                size="medium"
                icon={<StarsTwoFilled />}
                disabled={!isGenerateEnabled}
                testId="worksheet-create-with-ai-generate"
              >
                {t("common:generate_with_label", {
                  label: t("worksheet:questions"),
                })}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const curriculumType = getCurrentCurriculumProgramType(state);
  const academicYearId = getSelectedAcademicYearId(state);

  const portalType = _.get(state, "login.userInfo.portalType");

  const courseId =
    _.get(ownProps, "courseId") ||
    _.get(ownProps, "selectedCourse.courseId") ||
    _.get(state, "teacher.selected_class.selected_course");

  const courseGradeFilters = { academicYearId };
  const assessmentId = _.get(ownProps, "assessmentId");
  const parentType = _.get(state, "worksheet.parentEntity.type", null);

  const currentDebugInfoMetaData = _.get(
    state,
    "worksheet.aiAssessmentTools.debugInfoMetaData",
    null
  );

  const preStepCurrentDebugInfoMetaData = _.get(
    state,
    "worksheet.aiAssessmentTools.preStepDebugInfoMetaData",
    null
  );

  const worksheetMutationData = {
    worksheet: _.get(state, "worksheet", {}),
    parentId: assessmentId,
    parentType,
  };

  const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
    {
      parentId: assessmentId,
      parentType,
      portalType,
    }
  );

  const assessmentQuestionsQueryVariablesV2 = {
    id: assessmentId,
    type: parentType,
    portalType,
  };

  const collaboratorsFilter = { userAcademicYearId: academicYearId };

  return {
    curriculumType,
    courseId,
    courseGradeFilters,
    plannerElementSets: _.get(state, "platform.currentPlannerElementSets", []),
    portalType,
    parentId: assessmentId,
    parentType,
    worksheetMutationData,
    assessmentQuestionsQueryVariables,
    userType: getUserType(state),
    curriculumId: getSelectedCurriculumId(state),
    collaboratorsFilter,
    currentDebugInfoMetaData,
    preStepCurrentDebugInfoMetaData,
    assessmentQuestionsQueryVariablesV2,
  };
};

const mapActionCreators = {
  setToastMsg,
  onSendQuery,
  updateGeneratingAIQuestions,
  updateAIConversation,
};

export default compose(
  I18nHOC({ resource: ["ai", "common"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getAssessmentQuery, {
    name: "getAssessment",
    options: ({ portalType, collaboratorsFilter, assessmentId }) => ({
      // cache-first is used because this query will always get called in AssessmentDetails.js and latest data will be available in cache
      // if we use cache-and-network, it will make a network call and override the cache data
      // the reason to not use getAssessmentDetailsFromCache directly is this component won't get re-rendered if cache data changes
      fetchPolicy: "cache-first",
      variables: { id: assessmentId, collaboratorsFilter, portalType },
    }),
    props({
      getAssessment,
      ownProps: {
        t,
        mode,
        assessmentId,
        sourceFrom,
        curriculumType,
        courseId,
        selectedCourse,
        courseGradeFilters,
        unitPlanId,
        plannerElementSets,
        i18n,
        classSubjects,
        userType,
        curriculumId,
        creationMode,
      },
    }) {
      const assessmentDetails = _.get(getAssessment, "node", {});
      const aiConversationId = getAiConversationId(assessmentDetails);

      const course = !_.isEmpty(selectedCourse)
        ? selectedCourse
        : getCourseDetailsFromCache({
            id: courseId,
            courseGradeFilters,
          });

      const gradeId =
        _.get(course, "primaryGrade.id") || _.get(course?.grades, "0.id");

      const assessmentType = _.get(assessmentDetails, "assessmentType.value");
      const templateId = _.get(assessmentDetails, "templateId", null);
      const unitPlan = getUnitPlanFromAssessment({ assessmentDetails });
      const unitPlanTemplateId = _.get(unitPlan, "templateId", null);
      const presetAssessment = _.get(assessmentDetails, "presetAssessment", {});
      const assessmentObj = presetAssessment || assessmentDetails;
      const unitPlanDetails = getUnitDetailsFromUnitPlanField({
        assessmentObj,
        assessmentDetails,
        ownPropsUnitPlanId: unitPlanId,
      });
      const assessmentFields = _.get(assessmentDetails, "allFields", []);

      const { unitPlanFields } = unitPlanDetails || {};

      const unitType = getFieldValue({
        fields: unitPlanFields,
        uid: "unitType",
      });

      const resolvedSubjectNodes = _.get(
        _.find(unitPlanFields, { uid: "subjects" }),
        "resolvedMinimalTree.subjects",
        []
      );

      let templates = getPlannerTemplateFromCache(
        {
          ids: getTemplateArray([templateId]),
        },
        true
      );

      if (mode !== "view" && !_.isEmpty(templates)) {
        const unitPlanTemplate = getPlannerTemplateFromCache(
          getVariablesForLocalisedPlannerTemplates({
            templates: [unitPlanTemplateId],
            language: i18n.language,
          })
        );
        templates = [...templates, unitPlanTemplate];
      }

      if (_.isEmpty(templates)) {
        templates = getPlannerTemplateFromCache(
          {
            ids: getTemplateArray([templateId]),
          },
          true
        );
      }

      const originalTemplate = _.get(
        _.find(templates, { id: templateId }),
        "body",
        {}
      );

      const isFromClassroom = getIsFromClassroom({
        sourceFrom,
        template: originalTemplate,
      });

      const dynamicTemplate = getDynamicTemplate({
        template: originalTemplate,
        unitType,
        t,
        curriculumType,
        plannerElementSets,
        subjects: classSubjects,
        creationMode,
        curriculumId,
      });

      const assessmentTemplate = updateAssessmentTemplate({
        t,
        template: dynamicTemplate,
        assessmentType,
        isFromClassroom,
        curriculumType,
        resourceFields: assessmentFields,
        mode: "edit",
        templateId,
        plannerElementSets,
        subjects: classSubjects,
        userType,
        assessmentTools: _.get(assessmentDetails, "assessmentTools", []),
      });

      let _unitPlanTemplate = _.find(templates, { id: unitPlanTemplateId });

      if (_.isEmpty(_unitPlanTemplate) && !_.isEmpty(unitPlanTemplateId)) {
        _unitPlanTemplate = getPlannerTemplateFromCache(
          getVariablesForLocalisedPlannerTemplates({
            templates: [unitPlanTemplateId],
            language: i18n.language,
          })
        );
      }

      const unitPlanTemplate = _.get(
        changeDisciplinaryContentToMultiField({
          t,
          template: _unitPlanTemplate,
          unitType: getFieldValue({
            fields: _.get(unitPlanDetails, "unitPlanFields", []),
            uid: "unitType",
          }),
          curriculumType,
          resolvedSubjectNodes,
        }),
        "body",
        {}
      );

      const stepList = _.get(assessmentTemplate, "step_list", {});
      const unitPlanFieldList = _.get(unitPlanTemplate, "field_list", {});
      const unitPlanStepList = _.get(unitPlanTemplate, "step_list", {});
      const fieldList = _.get(assessmentTemplate, "field_list", {});

      const defaultOptions = getOptionsFromField({
        fieldList,
        fieldUid: FIELD_UID_ADD_LEARNING_GOALS,
        t,
      });

      const learningGoalsDropdownOptions = getAddLearningGoalsDropdownOptions({
        options: defaultOptions,
        resourceFields: assessmentFields,
        unitPlanTemplate,
        subjects: classSubjects,
        fieldList,
      });

      const learningGoalsFieldsUIDs = _.map(
        learningGoalsDropdownOptions,
        "key"
      );

      const learningGoalsFields = _.pick(fieldList, learningGoalsFieldsUIDs);

      const onSendQueryProps = {
        leId: assessmentId,
        gradeId,
        aiConversationId,
        course,
        stepList,
        fieldList,
        unitPlanStepList,
        unitPlanFieldList,
      };

      const descriptionField = _.find(_.get(assessmentDetails, "allFields"), {
        uid: "description",
      });

      return {
        gradeId,
        descriptionField,
        aiConversationId,
        learningGoalsFields,
        onSendQueryProps,
        unitPlanDetails,
        assessmentTemplate,
        assessmentDetails,
      };
    },
  }),

  // @TODO, Check and Move this is question loader
  getAssessmentQuestionsEnhancer(),
  getAssessmentQuestionsEnhancerV2()
)(AiQuestionGeneratorV2);
